import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, Loader2, RefreshCw, Lock, Unlock, Wrench, CheckCircle2, AlertTriangle } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';

const fmt = (d) => new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const STATUS = {
  ok: { label: 'OK', cls: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
  'corrigé': { label: 'Corrigé automatiquement', cls: 'bg-amber-50 text-amber-700', icon: Wrench },
  alerte: { label: 'À traiter', cls: 'bg-rose-50 text-rose-700', icon: AlertTriangle },
};
const scoreColor = (s) => (s >= 85 ? '#059669' : s >= 60 ? '#d97706' : '#e11d48');

const ScoreCard = ({ report, scanning, onScan }) => (
  <section className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col sm:flex-row sm:items-center gap-6" data-testid="security-score-card">
    <div className="w-24 h-24 rounded-full flex items-center justify-center shrink-0 border-[6px]" style={{ borderColor: report ? scoreColor(report.score) : '#e5e7eb' }}>
      <span className="text-2xl font-black text-gray-900" data-testid="security-score">{report ? report.score : '—'}</span>
    </div>
    <div className="flex-1 min-w-0">
      <h2 className="text-lg font-bold text-gray-900">{report ? 'Dernier examen' : 'Aucun examen pour le moment'}</h2>
      {report && (
        <p className="text-sm text-gray-500 mt-1">
          {fmt(report.created_at)} · {report.triggered_by === 'automatique' ? 'examen du lundi' : 'lancé à la main'} · {report.environment} — {report.alerts} alerte(s), {report.fixes} correction(s) automatique(s)
        </p>
      )}
      <p className="text-xs text-gray-400 mt-2">Examen automatique chaque lundi à 8h. Réparations automatiques : verrouillage des comptes attaqués, remise en privé des fichiers exposés.</p>
    </div>
    <button onClick={onScan} disabled={scanning}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1e3a5f] hover:bg-[#16304f] text-white text-sm font-bold disabled:opacity-50 transition-colors"
      data-testid="security-scan-btn">
      {scanning ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Lancer un examen maintenant
    </button>
  </section>
);

const Findings = ({ findings }) => (
  <section className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50" data-testid="security-findings">
    {findings.map(f => {
      const s = STATUS[f.status];
      return (
        <div key={f.key} className="p-5 flex gap-4" data-testid={`security-finding-${f.key}`}>
          <s.icon size={18} className={`mt-0.5 shrink-0 ${s.cls.split(' ')[1]}`} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900">{f.title}</h3>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`} data-testid={`security-status-${f.key}`}>{s.label}</span>
              {f.status !== 'ok' && <span className="text-[11px] text-gray-400">gravité : {f.severity}</span>}
            </div>
            <p className="text-sm text-gray-600 mt-1">{f.detail}</p>
            {f.fix && <p className="text-xs text-amber-700 mt-1 font-medium">Action automatique : {f.fix}</p>}
          </div>
        </div>
      );
    })}
  </section>
);

const LockedAccounts = ({ locked, onUnlock }) => (
  <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="security-locked">
    <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3"><Lock size={15} className="text-rose-500" /> Comptes verrouillés</h2>
    {locked.length === 0 ? <p className="text-sm text-gray-500" data-testid="security-locked-empty">Aucun compte verrouillé.</p> : locked.map(u => (
      <div key={u.id} className="flex items-center gap-3 py-2 border-t border-gray-50 first:border-0" data-testid={`locked-${u.id}`}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{u.first_name} {u.last_name} <span className="text-gray-400 font-normal">· {u.email}</span></p>
          <p className="text-xs text-gray-500">{u.locked_reason} — jusqu'au {fmt(u.locked_until)}</p>
        </div>
        <button onClick={() => onUnlock(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-semibold hover:bg-gray-50" data-testid={`unlock-${u.id}`}>
          <Unlock size={12} /> Débloquer
        </button>
      </div>
    ))}
  </section>
);

const History = ({ reports }) => reports.length > 1 && (
  <section className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="security-history">
    <h2 className="text-sm font-bold text-gray-900 mb-3">Historique</h2>
    {reports.slice(1).map(r => (
      <p key={r.id} className="text-xs text-gray-600 py-1">
        <span className="font-bold" style={{ color: scoreColor(r.score) }}>{r.score}/100</span> · {fmt(r.created_at)} · {r.alerts} alerte(s), {r.fixes} correction(s)
      </p>
    ))}
  </section>
);

export default function SecurityWatchPage() {
  const [reports, setReports] = useState([]);
  const [locked, setLocked] = useState([]);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/security/reports`).then(r => setReports(r.data)).catch(() => {});
    axios.get(`${API}/admin/security/locked`).then(r => setLocked(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/admin/security/scan`);
      toast.success(`Examen terminé : score ${r.data.score}/100`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'examen a échoué");
    }
    setScanning(false);
  };

  const unlock = async (u) => {
    try {
      await axios.post(`${API}/admin/security/unlock/${u.id}`);
      toast.success(`Compte de ${u.email} débloqué`);
      load();
    } catch {
      toast.error('Déblocage impossible');
    }
  };

  const latest = reports[0];
  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-5xl space-y-6" data-testid="security-watch-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            {latest && latest.alerts ? <ShieldAlert className="text-rose-500" /> : <ShieldCheck className="text-emerald-600" />} Vigie sécurité
          </h1>
          <p className="text-sm text-gray-500 mt-1">Comptes, fichiers privés, chiffrement et configuration du serveur, vérifiés chaque semaine.</p>
        </div>
        <ScoreCard report={latest} scanning={scanning} onScan={scan} />
        {latest && <Findings findings={latest.findings} />}
        <LockedAccounts locked={locked} onUnlock={unlock} />
        <History reports={reports} />
      </div>
    </AdminLayout>
  );
}
